import { useEffect, useState, type ReactNode } from 'react';

import { ApiError } from '../api/client.js';
import type { ExportJob } from '../api/types.js';

/**
 * Your account: taking everything out, and leaving (M4.6.1, M4.6.2).
 *
 * **Export comes first, on the same screen, and deletion offers it again.**
 * D1 made export the only portability story there is, and the moment
 * somebody decides to leave is the moment it matters most. So the delete
 * flow does not open with a warning; it opens by asking whether they have
 * their export, and offers to make one.
 *
 * Deletion asks for the word "delete" typed out. A confirm button alone is a
 * click; this is the one action in the product that cannot be undone.
 */

/** How often to ask whether the export is ready. */
const POLL_INTERVAL_MS = 3_000;

/** When to stop asking. The job carries on; the page says how to come back. */
const POLL_TIMEOUT_MS = 15 * 60 * 1000;

export interface AccountScreenProps {
  readonly email: string | undefined;
  readonly onRequestExport: () => Promise<ExportJob>;
  readonly onPollExport: (jobId: string) => Promise<ExportJob>;
  readonly onDeleteAccount: () => Promise<{ completesBy: string }>;
  /** After a deletion is accepted: the session is over. */
  readonly onDeleted: () => void;
  readonly onClose: () => void;
}

export function AccountScreen(props: AccountScreenProps): ReactNode {
  const [job, setJob] = useState<ExportJob | undefined>(undefined);
  const [exportError, setExportError] = useState<string | undefined>(undefined);
  const [gaveUp, setGaveUp] = useState(false);
  const [stage, setStage] = useState<'idle' | 'confirming' | 'deleting' | 'deleted'>(
    'idle',
  );
  const [typed, setTyped] = useState('');
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);

  const working =
    job !== undefined && (job.status === 'queued' || job.status === 'running');
  const jobId = job?.jobId;

  useEffect(() => {
    if (!working || jobId === undefined) return undefined;

    let cancelled = false;
    const startedAt = Date.now();

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        setGaveUp(true);
        return;
      }
      try {
        const next = await props.onPollExport(jobId);
        if (!cancelled) setJob(next);
      } catch {
        // A failed poll is not a failed export; ask again next time.
      }
    };

    const timer = setInterval(() => {
      void tick();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [working, jobId, props]);

  const startExport = async (): Promise<void> => {
    setExportError(undefined);
    setGaveUp(false);
    try {
      setJob(await props.onRequestExport());
    } catch (error) {
      setExportError(describe(error, 'The export could not be started.'));
    }
  };

  const confirmDelete = async (): Promise<void> => {
    setDeleteError(undefined);
    setStage('deleting');
    try {
      await props.onDeleteAccount();
      setStage('deleted');
    } catch (error) {
      setStage('confirming');
      setDeleteError(
        describe(error, 'Your account could not be deleted. Nothing was removed.'),
      );
    }
  };

  if (stage === 'deleted') {
    return (
      <div
        className="overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Account deleted"
      >
        <div className="overlay__card">
          <h2 className="overlay__title">Your account is being deleted</h2>
          <p className="overlay__text">
            You have been signed out. Your subscriptions, reading history, saved articles
            and exports are being removed, and will all be gone within a day. There is
            nothing else you need to do.
          </p>
          <div className="overlay__actions">
            <button className="button button--primary" onClick={props.onDeleted}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Your account">
      <div className="overlay__card overlay__card--wide">
        <h2 className="overlay__title">Your account</h2>
        {props.email !== undefined && <p className="overlay__text">{props.email}</p>}
        <p className="overlay__text">
          This is a test version, rebuilt from time to time.{' '}
          <a href="/about/alpha.html" target="_blank" rel="noreferrer">
            What that means for your data
          </a>
          .
        </p>

        <section className="account__section" aria-labelledby="account-export">
          <h3 className="account__heading" id="account-export">
            Export everything
          </h3>
          <p className="overlay__text">
            One file with your subscriptions, what you have read, your stars and tags, and
            every article saved for you — as pages you can open without this app.
          </p>
          <ExportStatus job={job} gaveUp={gaveUp} />
          {exportError !== undefined && (
            <p className="account__error" role="alert">
              {exportError}
            </p>
          )}
          <div className="overlay__actions">
            <button
              className="button"
              disabled={working}
              onClick={() => {
                void startExport();
              }}
            >
              {job?.status === 'completed' || job?.status === 'failed'
                ? 'Make a new export'
                : working
                  ? 'Preparing…'
                  : 'Export everything'}
            </button>
          </div>
        </section>

        <section
          className="account__section account__section--danger"
          aria-labelledby="account-delete"
        >
          <h3 className="account__heading" id="account-delete">
            Delete your account
          </h3>
          {stage === 'idle' ? (
            <>
              <p className="overlay__text">
                Removes your account and everything in it. This cannot be undone.
              </p>
              <div className="overlay__actions">
                <button
                  className="button"
                  onClick={() => {
                    setStage('confirming');
                  }}
                >
                  Delete account…
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="overlay__text">
                {job?.status === 'completed'
                  ? 'Your export is ready above. Download it before you go — once your account is deleted, it is deleted too.'
                  : 'Before you go: do you have your export? Everything you have read and saved goes with the account, and exports are deleted with it. Use "Export everything" above first if you want a copy.'}
              </p>
              <label className="field">
                <span className="field__label">Type delete to confirm</span>
                <input
                  className="field__input"
                  value={typed}
                  autoComplete="off"
                  onChange={(event) => {
                    setTyped(event.target.value);
                  }}
                />
              </label>
              {deleteError !== undefined && (
                <p className="account__error" role="alert">
                  {deleteError}
                </p>
              )}
              <div className="overlay__actions">
                <button
                  className="button button--danger"
                  disabled={
                    typed.trim().toLowerCase() !== 'delete' || stage === 'deleting'
                  }
                  onClick={() => {
                    void confirmDelete();
                  }}
                >
                  {stage === 'deleting' ? 'Deleting…' : 'Delete my account'}
                </button>
                <button
                  className="button"
                  onClick={() => {
                    setStage('idle');
                    setTyped('');
                    setDeleteError(undefined);
                  }}
                >
                  Keep my account
                </button>
              </div>
            </>
          )}
        </section>

        <div className="overlay__actions">
          <button className="button" onClick={props.onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ExportStatus({
  job,
  gaveUp,
}: {
  job: ExportJob | undefined;
  gaveUp: boolean;
}): ReactNode {
  if (job === undefined) return null;

  if (job.status === 'queued' || job.status === 'running') {
    return (
      <p className="overlay__text" role="status">
        {gaveUp
          ? 'Still preparing. It will finish without this page open — come back here and choose Export everything to see it.'
          : 'Preparing your export. This can take a few minutes for a large account.'}
      </p>
    );
  }

  if (job.status === 'failed') {
    return (
      <p className="account__error" role="alert">
        {job.failure ?? 'The export could not be built. Please try again.'}
      </p>
    );
  }

  if (job.downloadUrl === null) {
    return (
      <p className="overlay__text" role="status">
        That export has expired. Make a new one.
      </p>
    );
  }

  return (
    <p className="overlay__text" role="status">
      Your export is ready
      {job.bytes === null ? '' : ` (${formatBytes(job.bytes)})`}.{' '}
      <a className="button button--primary" href={job.downloadUrl} download>
        Download
      </a>
      {job.expiresAt !== null && (
        <span className="field__hint">
          Available until {new Date(job.expiresAt).toLocaleDateString()}.
        </span>
      )}
    </p>
  );
}

function describe(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.problem.detail !== undefined) {
    return error.problem.detail;
  }
  return fallback;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
